import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateField, TimeField } from '@/shared/ui';

import { MAX_INTERVAL, type RecurrenceError, toggleWeekday } from '@/entities/recurrence/model';
import { FREQUENCIES, type RecurrenceDraft } from '@/entities/recurrence/types';
import styles from './RecurrenceFields.module.css';

interface Props {
  draft: RecurrenceDraft;
  onChange: (draft: RecurrenceDraft) => void;
  errors: readonly RecurrenceError[];
  /** IANA zone the schedule runs in (shown, never edited here). */
  timezone: string | null;
  /** Locale for the weekday names. */
  locale: string;
}

const UNIT_KEY = {
  daily: 'recurrence.unitDays',
  weekly: 'recurrence.unitWeeks',
  monthly: 'recurrence.unitMonths',
  yearly: 'recurrence.unitYears',
} as const;

/** 2024-01-01 was a Monday: day 0 = Monday ... 6 = Sunday. */
function weekdayName(day: number, locale: string, width: 'short' | 'long'): string {
  return new Intl.DateTimeFormat(locale, { weekday: width, timeZone: 'UTC' }).format(
    new Date(Date.UTC(2024, 0, 1 + day)),
  );
}

/**
 * The schedule of a recurring task: every N days / weeks (on chosen weekdays) / months / years,
 * from a start date at a time of day, until an optional end date. Presentational: the caller
 * owns the draft and saves it (the card dialog, or Advanced Create before the card exists).
 */
export function RecurrenceFields({ draft, onChange, errors, timezone, locale }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const set = (patch: Partial<RecurrenceDraft>) => {
    onChange({ ...draft, ...patch });
  };
  const has = (...codes: RecurrenceError[]) => codes.some((code) => errors.includes(code));
  const errorId = `${id}-errors`;
  // The number field keeps what is typed (an empty box must stay editable, not snap back).
  const [intervalText, setIntervalText] = useState(String(draft.interval));

  return (
    <div className={styles.fields} data-testid="recurrence-fields">
      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>{t('recurrence.repeat')}</span>
          <select
            className={styles.control}
            value={draft.frequency}
            onChange={(event) => {
              const frequency = FREQUENCIES.find((value) => value === event.target.value);
              if (frequency) set({ frequency });
            }}
          >
            {FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {t(`recurrence.frequency.${frequency}`)}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.field}>
          <span className={styles.label}>{t('recurrence.every')}</span>
          <span className={styles.inline}>
            <input
              className={`${styles.control} ${styles.number}`}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_INTERVAL}
              value={intervalText}
              aria-invalid={has('interval')}
              aria-describedby={errorId}
              onChange={(event) => {
                setIntervalText(event.target.value);
                set({
                  interval: event.target.value === '' ? Number.NaN : Number(event.target.value),
                });
              }}
            />
            <span>{t(UNIT_KEY[draft.frequency], { count: draft.interval })}</span>
          </span>
        </label>
      </div>

      {draft.frequency === 'weekly' && (
        <fieldset className={styles.weekdays} aria-describedby={errorId}>
          <legend className={styles.label}>{t('recurrence.onDays')}</legend>
          <div className={styles.days}>
            {[0, 1, 2, 3, 4, 5, 6].map((day) => {
              const on = draft.weekdays.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  aria-label={weekdayName(day, locale, 'long')}
                  className={`${styles.day} ${on ? styles.dayOn : ''}`}
                  onClick={() => {
                    set({ weekdays: toggleWeekday(draft.weekdays, day) });
                  }}
                >
                  {weekdayName(day, locale, 'short')}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}

      <div className={styles.row}>
        <DateField
          label={t('recurrence.startDate')}
          value={draft.startDate}
          invalid={has('startRequired', 'startInvalid')}
          describedBy={errorId}
          onChange={(startDate) => {
            set({ startDate });
          }}
        />
        <TimeField
          label={t('recurrence.timeOfDay')}
          value={draft.timeOfDay}
          invalid={has('time')}
          describedBy={errorId}
          onChange={(timeOfDay) => {
            set({ timeOfDay });
          }}
        />
      </div>

      <fieldset className={styles.weekdays}>
        <legend className={styles.label}>{t('recurrence.endCondition')}</legend>
        {(['never', 'date'] as const).map((mode) => (
          <label key={mode} className={styles.check}>
            <input
              type="radio"
              name={`${id}-end`}
              checked={draft.endMode === mode}
              onChange={() => {
                set({
                  endMode: mode,
                  untilDate: mode === 'date' ? draft.untilDate || draft.startDate : draft.untilDate,
                });
              }}
            />
            {t(mode === 'never' ? 'recurrence.noEnd' : 'recurrence.endOnDate')}
          </label>
        ))}
      </fieldset>
      {draft.endMode === 'date' && (
        <DateField
          label={t('recurrence.endDate')}
          value={draft.untilDate}
          invalid={has('untilRequired', 'untilInvalid', 'untilBeforeStart')}
          describedBy={errorId}
          onChange={(untilDate) => {
            set({ untilDate });
          }}
        />
      )}

      {errors.length > 0 && (
        <p id={errorId} role="alert" className={styles.error}>
          {errors.map((code) => t(`recurrence.errors.${code}`)).join(' ')}
        </p>
      )}
      <p className={styles.hint}>
        {t('recurrence.hint')}
        {timezone ? ` ${t('deadline.zoneHint', { zone: timezone })}` : ''}
      </p>
    </div>
  );
}
